import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as ScrollLink } from "react-scroll";
import { motion } from "framer-motion";
import { IconType } from 'react-icons';
import {
    TbBrandWhatsapp, TbBuildingWarehouse, TbHome, TbInfoCircle,
    TbMenu2, TbPhoneCall, TbStars, TbX,
} from "react-icons/tb";

import logoAr from 'src/assets/images/navbar/logo_ar.webp';
import logoEn from 'src/assets/images/navbar/logo_en.webp';

const WHATSAPP_URL = "https://wa.me/218915160537";
const SCROLL_OFFSET = -76;

const MyNavbar = () => {
    const { t, i18n } = useTranslation('common');
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [active, setActive] = useState('hero');

    const logo = i18n.language === 'ar' ? logoAr : logoEn;

    const links: { to: string; label: string; icon: IconType }[] = [
        { to: "hero", label: t('nav.home'), icon: TbHome },
        { to: "aboutUs", label: t('nav.about'), icon: TbInfoCircle },
        { to: "services", label: t('nav.services'), icon: TbBuildingWarehouse },
        { to: "features", label: t('nav.features'), icon: TbStars },
    ];

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Lock page scroll & allow Escape while the mobile drawer is open
    useEffect(() => {
        document.body.style.overflow = open ? 'hidden' : '';
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
        if (open) document.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = '';
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    // Close the drawer when switching to desktop width
    useEffect(() => {
        const mq = window.matchMedia('(min-width: 1024px)');
        const onChange = () => mq.matches && setOpen(false);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    return (
        <>
            <header
                className={`sticky top-0 z-50 transition-all duration-300 ${scrolled
                    ? "bg-white/80 shadow-[0_10px_40px_-16px_rgba(14,60,98,.28)] backdrop-blur-xl backdrop-saturate-150"
                    : "bg-white"
                    }`}
            >
                {/* Brand accent line */}
                <div aria-hidden className="h-[3px] bg-gradient-to-r from-primary-300 via-primary-500 to-dark-primary-500" />

                <nav className="container flex h-16 items-center justify-between gap-6 lg:h-[76px]">
                    {/* Logo */}
                    <a
                        href="/"
                        className="shrink-0 rounded-lg transition-opacity hover:opacity-85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-400"
                        aria-label={t('store_name')}
                    >
                        <img
                            src={logo}
                            alt={t('store_name')}
                            className="h-8 w-auto sm:h-9 lg:h-10 xl:h-11"
                        />
                    </a>

                    {/* Desktop links */}
                    <div className="hidden items-center gap-4 lg:flex xl:gap-6">
                        <div className="flex items-center gap-1 rounded-full bg-gray-50 p-1.5 ring-1 ring-gray-100">
                            {links.map((link) => (
                                <ScrollLink
                                    key={link.to}
                                    to={link.to}
                                    href={`#${link.to}`}
                                    spy
                                    smooth
                                    duration={500}
                                    offset={SCROLL_OFFSET}
                                    onSetActive={() => setActive(link.to)}
                                    className={`relative cursor-pointer rounded-full px-5 py-2 text-[0.9375rem] tracking-brand transition-colors duration-300 ${active === link.to ? "font-normal text-primary-700" : "font-light text-body hover:text-primary-500"}`}
                                >
                                    {active === link.to && (
                                        <motion.span
                                            layoutId="nav-active-pill"
                                            transition={{ type: "spring", stiffness: 420, damping: 34 }}
                                            className="absolute inset-0 rounded-full bg-white shadow-[0_4px_14px_-4px_rgba(14,60,98,.22)] ring-1 ring-primary-100"
                                        />
                                    )}
                                    <span className="relative">{link.label}</span>
                                </ScrollLink>
                            ))}
                        </div>

                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group relative inline-flex h-12 items-center gap-2.5 overflow-hidden rounded-full bg-gradient-to-r from-primary-400 to-dark-primary-500 pe-6 ps-1.5 text-[0.9375rem] font-normal tracking-brand text-white shadow-glow transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_36px_-10px_rgba(66,154,237,.7)]"
                        >
                            {/* shine sweep */}
                            <span aria-hidden className="absolute inset-y-0 -left-full w-1/2 skew-x-[-20deg] bg-gradient-to-r from-transparent via-white/30 to-transparent transition-all duration-700 group-hover:left-[150%]" />
                            <span className="relative grid h-9 w-9 place-items-center rounded-full bg-white/20 ring-1 ring-white/30">
                                <span aria-hidden className="absolute inset-0 animate-ping rounded-full bg-white/20" />
                                <TbPhoneCall className="relative h-[18px] w-[18px] transition-transform duration-300 group-hover:rotate-12" />
                            </span>
                            <span className="relative">{t('nav.contact')}</span>
                        </a>
                    </div>

                    {/* Mobile actions */}
                    <div className="flex items-center gap-2 lg:hidden">
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={t('nav.contact')}
                            className="grid h-11 w-11 place-items-center rounded-2xl bg-gradient-to-br from-primary-400 to-dark-primary-500 text-white shadow-glow transition active:scale-95"
                        >
                            <TbPhoneCall className="h-5 w-5" />
                        </a>
                        <button
                            type="button"
                            onClick={() => setOpen(true)}
                            aria-label="Open menu"
                            aria-expanded={open}
                            aria-controls="mobile-drawer"
                            className="grid h-11 w-11 place-items-center rounded-2xl bg-gray-50 text-heading ring-1 ring-gray-100 transition hover:bg-primary-50 hover:text-primary-700 active:scale-95"
                        >
                            <TbMenu2 className="h-6 w-6" />
                        </button>
                    </div>
                </nav>
            </header>

            {/* Mobile drawer backdrop (outside <header>: backdrop-blur would trap fixed children) */}
            <div
                onClick={() => setOpen(false)}
                aria-hidden="true"
                className={`fixed inset-0 z-[60] h-dvh bg-primary-900/40 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${open ? "opacity-100" : "pointer-events-none opacity-0"}`}
            />

            {/* Mobile drawer */}
            <aside
                id="mobile-drawer"
                aria-hidden={!open}
                className={`fixed inset-y-0 end-0 z-[60] flex h-dvh w-[88%] max-w-sm flex-col bg-white shadow-2xl transition-[transform,visibility] duration-300 ease-out lg:hidden ${open ? "visible translate-x-0" : "invisible ltr:translate-x-full rtl:-translate-x-full"}`}
            >
                <div className="flex h-16 items-center justify-between border-b border-gray-100 px-6">
                    <img src={logo} alt={t("store_name")} className="h-8 w-auto" />
                    <button
                        type="button"
                        onClick={() => setOpen(false)}
                        aria-label="Close menu"
                        className="grid h-10 w-10 place-items-center rounded-full text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
                    >
                        <TbX className="h-6 w-6" />
                    </button>
                </div>

                <div className="flex flex-1 flex-col gap-1 overflow-y-auto p-4">
                    {links.map((link, i) => (
                        <ScrollLink
                            key={link.to}
                            to={link.to}
                            href={`#${link.to}`}
                            spy
                            smooth
                            duration={500}
                            offset={SCROLL_OFFSET}
                            onClick={() => setOpen(false)}
                            activeClass="!bg-primary-50 !text-primary-700"
                            style={{ transitionDelay: open ? `${80 + i * 40}ms` : "0ms" }}
                            className={`group flex cursor-pointer items-center gap-4 rounded-2xl px-4 py-3.5 text-lg font-light tracking-brand text-body transition-all duration-300 hover:bg-gray-50 hover:text-primary-500 ${open ? "translate-x-0 opacity-100" : "opacity-0 ltr:translate-x-4 rtl:-translate-x-4"}`}
                        >
                            <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary-50 text-primary-500 transition group-hover:bg-primary-400 group-hover:text-white">
                                <link.icon className="h-5 w-5" />
                            </span>
                            {link.label}
                        </ScrollLink>
                    ))}
                </div>

                <div className="border-t border-gray-100 p-5">
                    <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setOpen(false)}
                        className="flex h-14 w-full items-center justify-center gap-3 rounded-full bg-gradient-to-r from-primary-400 to-dark-primary-500 text-lg font-normal text-white shadow-glow transition active:scale-[.98]"
                    >
                        <TbBrandWhatsapp className="h-6 w-6" />
                        {t('nav.contact')}
                    </a>
                </div>
            </aside>
        </>
    );
};

export default MyNavbar;
